import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useImageUpload } from "../../hooks/useImageUpload";
import { useToastContext } from "../../containers/ToastContextWrapper";
import { useUserDetails } from "../../hooks/useUserDetails";
import { useLoadContacts } from "../../hooks/useLoadContacts";
import ThemedSpinner from "../shared/spinner";
import Typography from "../shared/typography";

interface RecordImageUploadProps {
  buttonLabel: string;
  message: string;
}

export default function RecordImageUpload({
  buttonLabel,
  message,
}: RecordImageUploadProps) {
  const { showToast } = useToastContext();
  const { userDetails } = useUserDetails();
  const { contacts } = useLoadContacts();
  const { userId: routeUserId } = useParams<{ userId?: string }>();
  const targetUserId = routeUserId ?? userDetails?.id;
  const activeContact = routeUserId
    ? contacts.find((contact) => contact.related_user_id === routeUserId)
    : null;

  const [status, setStatus] = useState<
    "idle" | "selecting" | "uploading" | "success" | "error"
  >("idle");

  const {
    pickImageFromFiles,
    triggerUpload,
    isPending,
  } = useImageUpload({
    userId: targetUserId || "",
    contactId: activeContact?.id,
    purpose: "medical_record",
    additionalDataProp: {
      description: message,
    },
    onUploadStart: () => setStatus("uploading"),
    onUploadSuccess: () => setStatus("success"),
    onUploadError: () => setStatus("error"),
  });

  const handleUpload = async () => {
    if (!targetUserId) {
      showToast({
        title: "Select a patient",
        description: "Choose whose medical records to upload to.",
        type: "error",
      });
      return;
    }

    setStatus("selecting");
    const selection = await pickImageFromFiles();
    if (selection && selection.length > 0) {
      triggerUpload();
    } else {
      setStatus("idle");
    }
  };

  return (
    <div className="mb-4">
      <div className="flex flex-wrap justify-end gap-3">
        <button className="flex items-center gap-2" onClick={handleUpload}>
          <PlusCircleIcon className="w-5 h-5 text-terracotta-500 transition-colors" />
          <Typography variant="body1" className="text-terracotta">
            {buttonLabel}
          </Typography>
        </button>
      </div>

      {(isPending || status === "uploading") && (
        <div className="mt-4 rounded-xl border border-beige-200 bg-beige-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Typography variant="subtitle2" className="text-brown-800">
                Uploading documents…
              </Typography>
              <Typography variant="body2" className="text-brown-600">
                We’ll add these to the medical records once the files finish
                processing.
              </Typography>
            </div>
            <ThemedSpinner size="md" />
          </div>
        </div>
      )}

      {status === "success" && !isPending && (
        <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4">
          <Typography variant="body2" className="text-green-700">
            Upload complete. It may take a moment for the new records to show
            up in the list below.
          </Typography>
        </div>
      )}

      {status === "error" && !isPending && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <Typography variant="body2" className="text-red-700">
            Upload failed. Please try again.
          </Typography>
        </div>
      )}
    </div>
  );
}
